// 거래명세서 목록 맨 위 요약 카드 3장 (2026-10-23 1단계 명세 §2·§3·§4-6·§5-1)
//   ① 청구 안 된 작업  ② 초안 만들 치과  ③ 받을 돈
// - 기공소 대표 계정에서만 렌더(목록이 판정해서 넘김). 받을 돈 숫자는 /transactions(PIN 구역) 화면 안에서만.
// - 목록을 먼저 그린 뒤 뒤에서 계산. 같은 세션 안에서는 다시 계산하지 않고 [다시 계산]으로만 다시 센다(①③ 캐시, ②는 가벼워 매번).
// - 카드에는 숫자·치과 이름·금액만 — 환자 이름 금지(펼침 표 안에서만 표시). console 에는 id·건수만.
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { computeUnbilledSummary, saveReview, deleteReview, DISPOSITIONS, DISPOSITION_LABEL } from '../../utils/missingClaims';
import { loadReceivables } from '../../utils/receivables';
import { getLabBilling } from '../../utils/billingCycle';
import { loadStatementsForClosing, classifyClosing, closingBlockedText } from './closingDraft';

const fmt = (n) => (Math.round(Number(n) || 0)).toLocaleString('ko-KR');
const md = (ymd) => (ymd ? `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}` : '');

// 세션 캐시 (labId → { unbilled, recv }) — 화면을 오가도 다시 계산하지 않는다
const sessionCache = new Map();
/** 발행·초안 삭제·삭제 표시·자동 초안 저장 뒤 호출 — 다음에 카드가 그려질 때 다시 계산 (받을 돈 원천 캐시는 invalidateReceivables가 따로) */
export function invalidateSummaryCache(labId) {
    if (labId) sessionCache.delete(labId); else sessionCache.clear();
}

const cardStyle = {
    flex: '1 1 240px', minWidth: 220, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
    padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4,
};
const cardBtn = (primary) => ({
    alignSelf: 'flex-start', marginTop: 6, padding: '6px 14px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
    border: primary ? 'none' : '1px solid #C7D2FE', background: primary ? '#6366F1' : '#EEF2FF', color: primary ? '#fff' : '#4338CA',
});
const smallBtn = (color = '#4338CA', border = '#C7D2FE') => ({
    padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 7, cursor: 'pointer', border: `1px solid ${border}`, background: '#fff', color,
});

// refreshKey: 목록이 같은 화면에서 명세서를 지웠을 때 올려 주면 카드를 다시 계산한다
function SummaryCards({ labId, ready, refreshKey = 0, onClosingAction, onOpenDraft, onAddToDraft, onReceivables, onGoPartners }) {
    const cached = sessionCache.get(labId) || {};
    const [unbilled, setUnbilled] = useState(cached.unbilled || null);   // computeUnbilledSummary 결과
    const [recv, setRecv] = useState(cached.recv || null);               // loadReceivables 결과
    const [closing, setClosing] = useState(null);                        // { rows, error }
    const [busy, setBusy] = useState({ unbilled: false, recv: false, closing: false });
    const [open, setOpen] = useState(null);                              // 'unbilled' | 'closing' | null

    const runUnbilled = useCallback(async (force = false) => {
        setBusy(b => ({ ...b, unbilled: true }));
        const r = await computeUnbilledSummary(labId, { now: new Date(), force });   // 실패도 throw 없이 error 로 옴. force = [다시 계산]만 명세서를 새로 읽기 (③과 공용 캐시, P1-4)
        setUnbilled(r);
        if (!r?.error) sessionCache.set(labId, { ...(sessionCache.get(labId) || {}), unbilled: r });   // 실패는 캐시하지 않음 — 다음에 다시 계산
        setBusy(b => ({ ...b, unbilled: false }));
    }, [labId]);
    const runRecv = useCallback(async (force = false) => {
        setBusy(b => ({ ...b, recv: true }));
        const r = await loadReceivables(labId, { force });   // 기본은 원장 캐시, [다시 계산]만 새로 읽기
        setRecv(r);
        if (!r?.error) sessionCache.set(labId, { ...(sessionCache.get(labId) || {}), recv: r });   // 실패는 캐시하지 않음
        setBusy(b => ({ ...b, recv: false }));
    }, [labId]);
    const runClosing = useCallback(async () => {
        setBusy(b => ({ ...b, closing: true }));
        try {
            const now = new Date();
            const billing = await getLabBilling(labId);
            const entries = Object.entries(billing.partners || {}).filter(([, v]) => v && v.closingDay);
            let rows = [];
            if (entries.length > 0) {
                const statements = await loadStatementsForClosing(labId, now);
                rows = entries.map(([pk, v]) => ({ partnerKey: pk, partnerName: v.partnerName || '', c: classifyClosing(pk, v.closingDay, statements, now) }))
                    .filter(r => r.c)
                    .sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'ko'));
            }
            setClosing({ rows, configured: entries.length, error: null });
        } catch (e) {
            console.error('마감 초안 대상 계산 실패:', e?.code || e?.message);
            setClosing({ rows: [], configured: 0, error: e?.code || e?.message || '계산 실패' });
        }
        setBusy(b => ({ ...b, closing: false }));
    }, [labId]);

    // 목록이 다 그려진 뒤 계산 시작
    useEffect(() => {
        if (!ready || !labId) return;
        if (!sessionCache.get(labId)?.unbilled) runUnbilled();
        if (!sessionCache.get(labId)?.recv) runRecv();
        runClosing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, labId]);

    // 받을 돈 결과를 목록 '입금' 열에 넘김
    useEffect(() => {
        if (recv && !recv.error && onReceivables) onReceivables(recv.byPartner || {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recv]);

    const recheckAll = () => { runUnbilled(true); runRecv(true); runClosing(); };
    // 목록에서 삭제 등으로 숫자가 바뀌면 다시 계산 (첫 렌더 0은 건너뜀)
    useEffect(() => {
        if (!refreshKey || !ready || !labId) return;
        runUnbilled(); runRecv(); runClosing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);
    const anyBusy = busy.unbilled || busy.recv || busy.closing;

    // ① 숫자는 행 상태에서 다시 센다 — [이유 고르기] 후 바로 줄어들게
    const rows = unbilled?.rows || [];
    const openRows = rows.filter(r => !r.review);
    const unbilledOpen = openRows.filter(r => r.kind === 'unbilled');
    const cnt1 = unbilledOpen.length;
    const amt1 = unbilledOpen.reduce((s, r) => s + (Number(r.estimate) || 0), 0);
    const unknown1 = unbilledOpen.filter(r => r.estimate == null || r.unpriced).length;
    const draftOnly1 = openRows.filter(r => r.kind === 'draft_only').length;

    // ②
    const closingRows = closing?.rows || [];
    const actionRows = closingRows.filter(r => r.c.state !== 'issued');
    const sameDraftN = closingRows.filter(r => r.c.state === 'sameDraft').length;
    const labels = [...new Set(actionRows.map(r => r.c.period.label))];

    // ③
    const byPartner = recv?.byPartner || {};
    const owing = Object.values(byPartner).filter(e => e.balance > 0);

    const updateRow = (row, review) => {
        setUnbilled(prev => {
            if (!prev) return prev;
            const next = { ...prev, rows: prev.rows.map(r => (r === row ? { ...r, review } : r)) };
            sessionCache.set(labId, { ...(sessionCache.get(labId) || {}), unbilled: next });
            return next;
        });
    };

    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>{'한눈에 보기'}</span>
                <button onClick={recheckAll} disabled={anyBusy}
                    style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, ...smallBtn('#475569', '#E2E8F0'), cursor: anyBusy ? 'wait' : 'pointer', opacity: anyBusy ? 0.6 : 1 }}>
                    <RefreshCw size={12} style={anyBusy ? { animation: 'spin 1s linear infinite' } : undefined} /> {anyBusy ? '계산 중…' : '다시 계산'}
                </button>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {/* ① 청구 안 된 작업 */}
                <div style={{ ...cardStyle, borderColor: open === 'unbilled' ? '#A5B4FC' : '#E2E8F0' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>{'① 청구 안 된 작업'}</div>
                    {!unbilled ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '6px 0' }}>{busy.unbilled ? '계산 중…' : '-'}</div>
                    ) : unbilled.error ? (
                        <div style={{ fontSize: 12.5, color: '#B91C1C', padding: '4px 0' }}>{'계산하지 못했습니다 — [다시 계산]을 눌러 주세요'}</div>
                    ) : (<>
                        <div style={{ fontSize: 20, fontWeight: 800, color: cnt1 > 0 ? '#9A3412' : '#0F172A' }}>
                            {`${cnt1}건`}
                            <span style={{ fontSize: 13.5, fontWeight: 700, marginLeft: 6 }}>{`· 약 ${fmt(amt1)}원(추정)`}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B' }}>
                            {`작성 중 명세서에만 ${draftOnly1}건`}{unknown1 > 0 ? ` · 단가 미상 ${unknown1}건` : ''}
                        </div>
                        <button onClick={() => setOpen(o => (o === 'unbilled' ? null : 'unbilled'))} style={cardBtn(cnt1 > 0)}>
                            {open === 'unbilled' ? '접기' : '펼쳐 보기'}
                        </button>
                    </>)}
                </div>

                {/* ② 초안 만들 치과 */}
                <div style={{ ...cardStyle, borderColor: open === 'closing' ? '#A5B4FC' : '#E2E8F0' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>{'② 초안 만들 치과'}</div>
                    {!closing ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '6px 0' }}>{busy.closing ? '계산 중…' : '-'}</div>
                    ) : closing.error ? (
                        <div style={{ fontSize: 12.5, color: '#B91C1C', padding: '4px 0' }}>{'계산하지 못했습니다 — [다시 계산]을 눌러 주세요'}</div>
                    ) : closing.configured === 0 ? (<>
                        <div style={{ fontSize: 12.5, color: '#64748B', padding: '4px 0', lineHeight: 1.6 }}>
                            {'청구 마감일을 정한 치과가 없습니다. 거래처 화면에서 정하면 마감 지난 치과가 여기에 뜹니다.'}
                        </div>
                        <button onClick={onGoPartners} style={cardBtn(false)}>{'거래처에서 정하기'}</button>
                    </>) : (<>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>
                            {`${actionRows.length}곳`}
                            {labels.length === 1 && <span style={{ fontSize: 13.5, fontWeight: 700, marginLeft: 6 }}>{`(${labels[0]})`}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B' }}>{`작성 중 ${sameDraftN}장`}</div>
                        <button onClick={() => setOpen(o => (o === 'closing' ? null : 'closing'))} style={cardBtn(actionRows.length > 0)}>
                            {open === 'closing' ? '접기' : '초안 만들기'}
                        </button>
                    </>)}
                </div>

                {/* ③ 받을 돈 */}
                <div style={cardStyle}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>{'③ 받을 돈'}</div>
                    {!recv ? (
                        <div style={{ fontSize: 13, color: '#94A3B8', padding: '6px 0' }}>{busy.recv ? '계산 중…' : '-'}</div>
                    ) : recv.error ? (
                        <div style={{ fontSize: 12.5, color: '#B91C1C', padding: '4px 0' }}>{'계산하지 못했습니다 — [다시 계산]을 눌러 주세요'}</div>
                    ) : (<>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{`${fmt(recv.total)}원`}</div>
                        <div style={{ fontSize: 12, color: '#64748B' }}>{`치과 ${owing.length}곳 · 입금은 직접 기록한 것만 반영됩니다`}</div>
                        <button onClick={onGoPartners} style={cardBtn(false)}>{'거래처별 보기'}</button>
                    </>)}
                </div>
            </div>

            {open === 'unbilled' && unbilled && !unbilled.error && (
                <UnbilledPanel labId={labId} summary={unbilled} rows={rows} onUpdateRow={updateRow} onAddToDraft={onAddToDraft} />
            )}
            {open === 'closing' && closing && !closing.error && (
                <ClosingPanel rows={closingRows} onClosingAction={onClosingAction} onOpenDraft={onOpenDraft} />
            )}
        </div>
    );
}

// ── ① 펼침 패널 — MissingClaims 표 방식 재사용(기간 입력 없음: 지난달까지·최근 3개월 고정) ──
function UnbilledPanel({ labId, summary, rows, onUpdateRow, onAddToDraft }) {
    const [filter, setFilter] = useState('unbilled');          // 'unbilled' | 'draft_only' | 'remake'
    const [openClinics, setOpenClinics] = useState(new Set());
    const [reasonFor, setReasonFor] = useState(null);         // 이유 고르기 펼친 행 caseKey
    const [saving, setSaving] = useState(null);
    const [showDone, setShowDone] = useState(false);

    const openRows = rows.filter(r => !r.review);
    const doneRows = rows.filter(r => !!r.review);
    const n = (k) => openRows.filter(r => r.kind === k).length;
    const list = openRows.filter(r => r.kind === filter);
    const groups = new Map();
    list.forEach(r => { if (!groups.has(r.clinicName)) groups.set(r.clinicName, []); groups.get(r.clinicName).push(r); });

    const choose = async (row, disposition) => {
        setSaving(row.caseKey);
        try {
            const rv = await saveReview(labId, row, disposition);
            onUpdateRow(row, { disposition: rv.disposition, docId: rv.docId, legacy: false });
            setReasonFor(null);
        } catch (e) {
            console.error('처분 저장 실패:', row.parsedCaseId, e?.code || e?.message);
            alert('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
        setSaving(null);
    };
    const undo = async (row) => {
        setSaving(row.caseKey);
        try {
            await deleteReview(labId, row);
            onUpdateRow(row, null);
        } catch (e) {
            console.error('처분 되돌리기 실패:', row.parsedCaseId, e?.code || e?.message);
            alert('되돌리지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
        setSaving(null);
    };

    const chip = (k, label) => (
        <button key={k} onClick={() => setFilter(k)}
            style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 16, cursor: 'pointer',
                border: filter === k ? '1.5px solid #6366F1' : '1px solid #E2E8F0', background: filter === k ? '#EEF2FF' : '#fff', color: filter === k ? '#4338CA' : '#475569' }}>
            {label}
        </button>
    );

    return (
        <div style={{ marginTop: 12, background: '#fff', border: '1px solid #C7D2FE', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>{'청구 안 된 작업 — 지난달까지 (최근 3개월)'}</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 3, lineHeight: 1.6 }}>
                {'수집한 작업 중 보낸 명세서에 없는 건입니다. 자동으로 확정하지 않습니다 — 의심 건이니 하나씩 확인해 주세요. 금액은 단가표로 계산한 추정치입니다.'}
            </div>
            {summary.neverBilledClinicCount > 0 && (
                <div style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>
                    {`덴클로 명세서를 보낸 적 없는 치과 ${summary.neverBilledClinicCount}곳은 세지 않았습니다`}
                </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
                {chip('unbilled', `청구 안 됨 ${n('unbilled')}`)}
                {chip('draft_only', `작성 중 명세서에만 ${n('draft_only')}건`)}
                {/* 리메이크 별도 줄 — 숫자만, 금액 없음 */}
                {chip('remake', `리메이크 ${n('remake')}건 — 무상/유상 확인 안 됨`)}
            </div>

            {list.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#059669', background: '#F0FDF4', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                    {'확인할 건이 없습니다.'}
                </div>
            ) : [...groups.entries()].map(([clinic, grp]) => {
                const isOpen = openClinics.has(clinic);
                const sum = grp.reduce((a, r) => a + (Number(r.estimate) || 0), 0);
                return (
                    <div key={clinic} style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                        <div onClick={() => setOpenClinics(prev => { const x = new Set(prev); if (x.has(clinic)) x.delete(clinic); else x.add(clinic); return x; })}
                            style={{ background: '#F8FAFC', padding: '10px 14px', display: 'flex', alignItems: 'baseline', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                            <span style={{ fontSize: 12, color: '#94A3B8', width: 14 }}>{isOpen ? '▾' : '▸'}</span>
                            <span style={{ fontWeight: 700, fontSize: 13.5, color: '#334155' }}>{clinic}</span>
                            <span style={{ fontSize: 12, color: '#94A3B8' }}>{`${grp.length}건`}</span>
                            {filter === 'unbilled' && sum > 0 && (
                                <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: '#7C2D12' }}>{`약 ${fmt(sum)}원`}</span>
                            )}
                        </div>
                        {isOpen && grp.map(r => (
                            <div key={r.caseKey} style={{ padding: '10px 14px', borderTop: '1px solid #F1F5F9' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                                    {/* 환자 이름 — 대표 PIN 화면 펼침 표 안에서만 표시(로그·카드 금지) */}
                                    <span style={{ fontWeight: 700, fontSize: 13.5, color: '#1F2937' }}>{r.patientName || '환자미상'}</span>
                                    {r.teeth ? <span style={{ fontSize: 12.5, color: '#64748B' }}>{`#${r.teeth}`}</span> : null}
                                    {r.prosthesisLabel ? <span style={{ fontSize: 12.5, color: '#475569' }}>{r.prosthesisLabel}</span> : null}
                                    {r.workDate ? <span style={{ fontSize: 12, color: '#94A3B8' }}>{md(r.workDate)}</span> : null}
                                    {r.wasInStatement && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#B91C1C', background: '#FEF2F2', borderRadius: 6, padding: '1px 7px' }}>{'지워진 명세서에 있었음'}</span>
                                    )}
                                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 13, color: r.estimate == null ? '#9CA3AF' : '#7C2D12' }}>
                                        {r.kind === 'remake' ? '' : (r.estimate == null ? '단가 미상' : `약 ${fmt(r.estimate)}원`)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                    {r.kind !== 'draft_only' && (
                                        <button onClick={() => onAddToDraft(r)} style={{ ...smallBtn('#fff', '#6366F1'), background: '#6366F1' }}>{'이번 초안에 넣기'}</button>
                                    )}
                                    <button onClick={() => setReasonFor(k => (k === r.caseKey ? null : r.caseKey))} disabled={saving === r.caseKey} style={smallBtn()}>
                                        {reasonFor === r.caseKey ? '이유 접기' : '이유 고르기'}
                                    </button>
                                    {reasonFor === r.caseKey && DISPOSITIONS.map(d => (
                                        <button key={d.key} disabled={saving === r.caseKey} onClick={() => choose(r, d.key)}
                                            style={{ ...smallBtn(d.color, `${d.color}55`), opacity: saving === r.caseKey ? 0.5 : 1 }}>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })}

            {doneRows.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <button onClick={() => setShowDone(v => !v)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>
                        {`처리됨 ${doneRows.length}건 ${showDone ? '▴' : '▾'}`}
                    </button>
                    {showDone && doneRows.map(r => (
                        <div key={r.caseKey} style={{ padding: '8px 4px', borderTop: '1px solid #F8FAFC', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                            <span style={{ color: '#94A3B8' }}>{r.clinicName}</span>
                            <span style={{ fontWeight: 600, color: '#475569' }}>{r.patientName || '환자미상'}</span>
                            {r.teeth ? <span style={{ color: '#94A3B8' }}>{`#${r.teeth}`}</span> : null}
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', borderRadius: 6, padding: '2px 8px' }}>
                                {DISPOSITION_LABEL[r.review?.disposition] || r.review?.disposition}
                            </span>
                            <button disabled={saving === r.caseKey} onClick={() => undo(r)}
                                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#6366F1', fontSize: 12, cursor: 'pointer' }}>{'되돌리기'}</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── ② 펼침 패널 — 마감 지난 치과 줄 ──
function ClosingPanel({ rows, onClosingAction, onOpenDraft }) {
    const [showIssued, setShowIssued] = useState(false);
    const action = rows.filter(r => r.c.state !== 'issued');
    const issued = rows.filter(r => r.c.state === 'issued');
    const line = (r) => {
        const { c } = r;
        const range = `${md(c.period.from)}~${md(c.period.to)}`;
        return (
            <div key={r.partnerKey} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid #F1F5F9', fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: '#0F172A', minWidth: 120 }}>{r.partnerName || '이름 없음'}</span>
                <span style={{ color: '#64748B' }}>{`${c.period.label} · ${range}`}</span>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {c.state === 'issued' && <span style={{ color: '#15803D', fontWeight: 700 }}>{'발행됨 ✓'}</span>}
                    {c.state === 'none' && (
                        <button onClick={() => onClosingAction(r.partnerKey, r.partnerName, c)} style={{ ...smallBtn('#fff', '#6366F1'), background: '#6366F1' }}>{'초안 만들기'}</button>
                    )}
                    {c.state === 'sameDraft' && (<>
                        <span style={{ color: '#4338CA', fontWeight: 700 }}>{'작성 중'}</span>
                        <button onClick={() => onClosingAction(r.partnerKey, r.partnerName, c)} style={smallBtn()}>{'새 수집분 담기'}</button>
                    </>)}
                    {(c.state === 'otherDraft' || c.state === 'correction') && (<>
                        <span style={{ color: '#B45309', fontWeight: 600 }}>{closingBlockedText(c)}</span>
                        <button onClick={() => onOpenDraft(c.statementId)} style={smallBtn('#B45309', '#FDE68A')}>
                            {'그 명세서 열기'}
                        </button>
                    </>)}
                </span>
            </div>
        );
    };
    return (
        <div style={{ marginTop: 12, background: '#fff', border: '1px solid #C7D2FE', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px 8px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A' }}>{'청구 마감이 지난 치과'}</div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 3 }}>
                    {'[초안 만들기]를 누르면 그 기간의 의뢰서·수집분을 담아 바로 임시저장합니다. 발행은 확인 후 직접 누르세요.'}
                </div>
            </div>
            {action.length === 0 ? (
                <div style={{ padding: '14px', fontSize: 13, color: '#059669', borderTop: '1px solid #F1F5F9' }}>{'지금 만들 초안이 없습니다.'}</div>
            ) : action.map(line)}
            {issued.length > 0 && (
                <div style={{ borderTop: '1px solid #F1F5F9', padding: '8px 14px' }}>
                    <button onClick={() => setShowIssued(v => !v)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#64748B' }}>
                        {`발행됨 ${issued.length}곳 ${showIssued ? '▴' : '▾'}`}
                    </button>
                </div>
            )}
            {showIssued && issued.map(line)}
        </div>
    );
}

export default SummaryCards;
